import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import redeem_services from "services/redeem_services";
import { networkTypeSelector } from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { sumForToken } from "utils/tokens";
import { CreateRedeemPayload, RedeemRequest } from "types/redeem";

export const MIN_REDEEM = 1;

export function useRedeemData(merchantId: string) {
  const queryClient = useQueryClient();
  const networkType = useSelector(networkTypeSelector);
  const { param: token } = useSettlementToken();

  // /redeem/active now filters by token server-side, so the token MUST be in the key — the
  // response genuinely differs per token, and staleTime is Infinity. Invalidations elsewhere use
  // the ["redeems", merchantId] prefix, which still matches.
  const requestsQuery = useQuery({
    queryKey: ["redeems", merchantId, token],
    queryFn: () =>
      redeem_services.getRedeemRequests(merchantId, networkType, token),
    enabled: !!merchantId,
  });

  const submitMutation = useMutation({
    mutationFn: (payload: CreateRedeemPayload) =>
      redeem_services.createRedeemRequest(payload, networkType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["merchantBalance"] });
      queryClient.invalidateQueries({ queryKey: ["redeems", merchantId] });
    },
  });

  // Rows come back already scoped to `token`. sumForToken is kept rather than a plain reduce
  // because it still guards the two cases the server cannot: no token selected (returns every
  // token, so every row counts) and a row the backend could not tag.
  const requests: RedeemRequest[] = requestsQuery.data ?? [];
  const requestsBalance: number = sumForToken(requests, token);

  return {
    requests,
    pendingRedeemsBalance: requestsBalance,
    requestsLoading: requestsQuery.isLoading,
    refreshRequests: () =>
      queryClient.invalidateQueries({ queryKey: ["redeems", merchantId] }),
    submitRedeem: submitMutation.mutate,
    submitLoading: submitMutation.isPending,
  };
}
